import { Button, ButtonType } from "components/atoms"
import { ToCurrency } from "lib/CurrencyFormatter"
import { LogGaEvent } from "lib/GoogleAnalytics"
import { EventAction, EventCategory, EventLabel } from "models/GoogleAnalytics"
import { CompletionDateWarning } from "pages/Application/Status/RateCheckPromise/CompletionDateWarning"
import React, { useState } from "react"
import styled from "styled-components"
import IconTick from "url:assets/images/icons/Icon_Tick.svg"
import { ILRState, ProductDetails, ProductDetailsRequest } from "../types"
import { ArrowColor, Arrows } from "./RateCheckerPage"
import { RateDetail, RateDetailEnum } from "./RateDetail"
import { RateRepresentativeExample } from "./RepExample"

interface RateCardProps {
  currentRate: boolean
  currentInitialCost: number
  productDetails: ProductDetails
  arrows: Arrows
  productInput: ProductDetailsRequest
  identifiedLowerRateStatus?: ILRState | undefined
  identifiedLowerRateId?: string | undefined
  isError: boolean
  setIsError: React.Dispatch<React.SetStateAction<boolean>>
  actionClicked: null | "current" | "new"
  setActionClicked: React.Dispatch<React.SetStateAction<null | "current" | "new">>
  handleClick: () => void
  handleEsisDownload: () => void
  showCompletionDateWarning?: boolean
  warningChecked: boolean
  handleWarningCheckboxChange: () => void
}

const formatProductEndDate = (date: Date) => {
  return date.toLocaleString("default", { month: "short", year: "numeric" })
}

/** @param {ILRState} status if a better rate has been identified, this is its state
 * @param {undefined} status if no better rate has been identified
 */
const mapStatusToFlags = (status: ILRState | undefined) => {
  const declined = status === ILRState.Declined
  const expired = status === ILRState.Expired
  const completed = status === ILRState.Completed
  const progressing = status === ILRState.Progressing
  const cancelled = status === ILRState.Cancelled
  // Found exists. FYI :p
  const currentRateSelected = expired || declined || cancelled || status == undefined
  const otherRateSelected = progressing || completed
  const active = !expired && !completed && !cancelled
  return {
    declined,
    expired,
    completed,
    progressing,
    cancelled,
    currentRateSelected,
    otherRateSelected,
    active,
  }
}

export const RateCard = ({
  productDetails,
  arrows,
  productInput: { loanAmount, termMonths, addFeesToLoan },
  identifiedLowerRateStatus,
  identifiedLowerRateId,
  actionClicked,
  handleClick,
  isError,
  currentRate,
  currentInitialCost,
  handleEsisDownload,
  showCompletionDateWarning,
  warningChecked,
  handleWarningCheckboxChange,
}: RateCardProps) => {
  const [buttonClicked, setButtonClicked] = useState(false)
  const {
    currentRateSelected,
    otherRateSelected,
    active,
    expired,
    completed,
    progressing,
    cancelled,
    declined,
  } = mapStatusToFlags(identifiedLowerRateStatus)

  const {
    AprLenders,
    ArrangementFee,
    BookingFee,
    FeesTotal,
    InitialMonthlyPayment,
    InitialPayRate,
    InitialRatePeriodPrecise,
    InitialRatePeriodMonths,
    MonthlyPaymentAfterInitialPeriod,
    StandardVariableRate,
    TotalInterestPayable,
    TrueCost,
    TrueCostOverInitialPeriod,
    InitialMonthlyPaymentIncludingFees,
  } = productDetails
  const formattedMonthlyPayment = addFeesToLoan
    ? ToCurrency(InitialMonthlyPaymentIncludingFees)
    : ToCurrency(InitialMonthlyPayment)
  const formattedInitialCosts = ToCurrency(TrueCostOverInitialPeriod)
  const formattedEndDate = formatProductEndDate(new Date(InitialRatePeriodPrecise))
  const formattedBorrowing = ToCurrency(loanAmount)
  const formattedTrueCosts = ToCurrency(TrueCost)
  const formattedArrangementFee = ToCurrency(ArrangementFee)
  const feesOther = FeesTotal - BookingFee
  const formattedFeesOther = ToCurrency(feesOther)
  const formattedInterest = ToCurrency(TotalInterestPayable)
  const formatMonthlyPaymentAfterInitialPeriod = ToCurrency(MonthlyPaymentAfterInitialPeriod)

  const selected =
    (currentRate && currentRateSelected && active) || (!currentRate && otherRateSelected && active)
  const thisRateSelected = buttonClicked && !isError
  const isOverallInitialCostHigher =
    currentInitialCost && TrueCostOverInitialPeriod > currentInitialCost

  const overallSelected = selected || thisRateSelected

  const warningNotChecked: boolean = !!showCompletionDateWarning && !warningChecked

  const disable =
    (currentRate && (actionClicked === "new" || otherRateSelected || warningNotChecked)) ||
    (!currentRate && (actionClicked === "current" || currentRateSelected))

  const cardTitle = currentRate
    ? "Your current rate"
    : expired
    ? "Expired rate"
    : declined
    ? "Declined rate"
    : "New rate"

  const handleRateClick = () => {
    handleClick()
    setButtonClicked(true)
    LogGaEvent({
      action: EventAction.buttonClicked,
      event_category: EventCategory.functionalInteraction,
      event_label: currentRate ? EventLabel.autoRateDecline : EventLabel.autoRateAccept,
    })
  }

  return (
    <RateWrapper selected={overallSelected} disable={disable}>
      <RateCardTitle>{cardTitle}</RateCardTitle>
      {
        <>
          <RateContainer>
            <RateHighlight>
              <ValueContainer>
                <p>{InitialPayRate} %</p>
                <Arrow currentRate={currentRate} colour={arrows.initialRate} disable={disable} />
              </ValueContainer>
              <div>Initial interest rate</div>
            </RateHighlight>
            <RateDetails aria-label={`${cardTitle} details`}>
              <RateDetail
                type={RateDetailEnum.MONTHLY_PAYMENT}
                data={formattedMonthlyPayment}
                currentRate={currentRate}
                arrow={arrows.monthlyPayment}
                disable={disable}
              />
              <RateDetail
                type={RateDetailEnum.OVERALL_INITIAL_COST}
                data={formattedInitialCosts}
                currentRate={currentRate}
                arrow={arrows.overallCost}
                disable={disable}
              >
                {!currentRate && isOverallInitialCostHigher && (
                  <a
                    href="#faq-7"
                    style={{ color: "blue" }}
                    onClick={(e) => {
                      e.preventDefault()
                      const faqButtons = document.getElementsByClassName(
                        "faq-7-button"
                      ) as HTMLCollectionOf<HTMLButtonElement>
                      if (faqButtons.length > 0) {
                        faqButtons[0]?.click()
                        faqButtons[0]?.scrollIntoView({ behavior: "smooth", block: "center" })
                      }
                    }}
                  >
                    Why is this higher?
                  </a>
                )}
              </RateDetail>
              <RateDetail
                type={RateDetailEnum.APRC}
                data={`${AprLenders} %`}
                currentRate={currentRate}
                arrow={arrows.aprc}
                disable={disable}
              />
              <RateDetail
                type={RateDetailEnum.PRODUCT_FEE}
                data={`£${ArrangementFee}`}
                currentRate={currentRate}
                arrow={arrows.productFee}
                disable={disable}
              />
              <RateDetail
                type={RateDetailEnum.END_DATE}
                data={`${formattedEndDate}`}
                currentRate={currentRate}
                arrow={arrows.endDate}
                disable={disable}
              />
            </RateDetails>
          </RateContainer>
          {active && (
            <RateCTAs>
              {overallSelected ? (
                <RateActionContainer>
                  <div>{currentRate ? "Your current rate selected" : "New rate selected"}</div>
                </RateActionContainer>
              ) : currentRate ? (
                <CurrentRateCTA
                  disabled={disable || !identifiedLowerRateId}
                  handleRateClick={handleRateClick}
                />
              ) : (
                <NewRateCTA
                  disabled={disable || !identifiedLowerRateId || warningNotChecked}
                  handleRateClick={handleRateClick}
                  showWarning={!!showCompletionDateWarning}
                  handleWarningCheckboxChange={handleWarningCheckboxChange}
                  warningChecked={warningChecked}
                />
              )}
            </RateCTAs>
          )}
          <RateRepresentativeExample
            formattedBorrowing={formattedBorrowing}
            formattedTrueCosts={formattedTrueCosts}
            AprLenders={AprLenders}
            formattedArrangementFee={formattedArrangementFee}
            feesOther={feesOther}
            formattedFeesOther={formattedFeesOther}
            InitialPayRate={InitialPayRate}
            InitialRatePeriodMonths={InitialRatePeriodMonths}
            formattedMonthlyPayment={formattedMonthlyPayment}
            formattedInterest={formattedInterest}
            formatMonthlyPaymentAfterInitialPeriod={formatMonthlyPaymentAfterInitialPeriod}
            StandardVariableRate={StandardVariableRate}
            termMonths={termMonths}
          />
          <DownloadEsis
            handleClick={handleEsisDownload}
            disabled={
              (currentRate &&
                (progressing ||
                  completed)) /* disable for current rates that the customer doesn't want */ ||
              (!currentRate &&
                (expired ||
                  declined ||
                  cancelled)) /* disable for new rates that the customer can't have */
            }
          />
        </>
      }
    </RateWrapper>
  )
}

interface CTAProps {
  disabled: boolean
  handleRateClick: () => void
}

interface NewRateCTAProps extends CTAProps {
  showWarning: boolean
  handleWarningCheckboxChange: () => void
  warningChecked: boolean
}

const CurrentRateCTA = ({ disabled, handleRateClick }: CTAProps) => (
  <Button
    id={`button-select-current-rate`}
    disabled={disabled}
    type={ButtonType.Solid}
    onClick={handleRateClick}
    text={"Stay with this rate"}
  />
)

const NewRateCTA = ({
  disabled,
  handleRateClick,
  showWarning,
  handleWarningCheckboxChange,
  warningChecked,
}: NewRateCTAProps) => {
  return (
    <CompletionDateWarning
      checked={warningChecked}
      showWarning={showWarning}
      onChange={handleWarningCheckboxChange}
    >
      <Button
        id={`button-select-this-rate`}
        disabled={disabled}
        type={ButtonType.Solid}
        onClick={handleRateClick}
        text={"Request this rate"}
      />
    </CompletionDateWarning>
  )
}

interface FinalRateCardProps {
  productDetails: ProductDetails
  productInput: ProductDetailsRequest
}

export const FinalRateCard = ({
  productDetails: {
    AprLenders,
    ArrangementFee,
    BookingFee,
    FeesTotal,
    InitialMonthlyPayment,
    InitialPayRate,
    InitialRatePeriodPrecise,
    InitialRatePeriodMonths,
    MonthlyPaymentAfterInitialPeriod,
    StandardVariableRate,
    TotalInterestPayable,
    TrueCost,
    TrueCostOverInitialPeriod,
    InitialMonthlyPaymentIncludingFees,
  },
  productInput: { loanAmount, termMonths, addFeesToLoan },
}: FinalRateCardProps) => {
  const formattedMonthlyPayment = addFeesToLoan
    ? ToCurrency(InitialMonthlyPaymentIncludingFees)
    : ToCurrency(InitialMonthlyPayment)
  const formattedInitialCosts = ToCurrency(TrueCostOverInitialPeriod)
  const formattedEndDate = formatProductEndDate(new Date(InitialRatePeriodPrecise))
  const formattedBorrowing = ToCurrency(loanAmount)
  const formattedTrueCosts = ToCurrency(TrueCost)
  const formattedArrangementFee = ToCurrency(ArrangementFee)
  const feesOther = FeesTotal - BookingFee
  const formattedFeesOther = ToCurrency(feesOther)
  const formattedInterest = ToCurrency(TotalInterestPayable)
  const formatMonthlyPaymentAfterInitialPeriod = ToCurrency(MonthlyPaymentAfterInitialPeriod)
  const currentRate = true

  return (
    <RateWrapper selected={false} disable={false}>
      <RateCardTitle>Your final rate</RateCardTitle>
      <p>This is the rate you’ll move onto when you complete</p>
      <>
        <RateContainer>
          <RateHighlight>
            <ValueContainer>
              <p>{InitialPayRate} %</p>
            </ValueContainer>
            <div>Initial interest rate</div>
          </RateHighlight>
          <RateDetails aria-label="Final rate details">
            <RateDetail
              type={RateDetailEnum.MONTHLY_PAYMENT}
              data={formattedMonthlyPayment}
              currentRate={currentRate}
            />
            <RateDetail
              type={RateDetailEnum.OVERALL_INITIAL_COST}
              data={formattedInitialCosts}
              currentRate={currentRate}
            />
            <RateDetail
              type={RateDetailEnum.APRC}
              data={`${AprLenders} %`}
              currentRate={currentRate}
            />
            <RateDetail
              type={RateDetailEnum.PRODUCT_FEE}
              data={`£${ArrangementFee}`}
              currentRate={currentRate}
            />
            <RateDetail
              type={RateDetailEnum.END_DATE}
              data={`${formattedEndDate}`}
              currentRate={currentRate}
            />
          </RateDetails>
        </RateContainer>
        <RateRepresentativeExample
          formattedBorrowing={formattedBorrowing}
          formattedTrueCosts={formattedTrueCosts}
          AprLenders={AprLenders}
          formattedArrangementFee={formattedArrangementFee}
          feesOther={feesOther}
          formattedFeesOther={formattedFeesOther}
          InitialPayRate={InitialPayRate}
          InitialRatePeriodMonths={InitialRatePeriodMonths}
          formattedMonthlyPayment={formattedMonthlyPayment}
          formattedInterest={formattedInterest}
          formatMonthlyPaymentAfterInitialPeriod={formatMonthlyPaymentAfterInitialPeriod}
          StandardVariableRate={StandardVariableRate}
          termMonths={termMonths}
        />
      </>
    </RateWrapper>
  )
}

const DownloadEsis = ({
  handleClick,
  disabled,
}: {
  handleClick: () => void
  disabled?: boolean
}) => {
  return (
    <DownloadEsisContainer>
      {disabled ? (
        <DownloadEsisLinkDisabled>Download Mortgage Illustration</DownloadEsisLinkDisabled>
      ) : (
        <DownloadEsisLink target="_blank" onClick={handleClick}>
          Download Mortgage Illustration
        </DownloadEsisLink>
      )}
    </DownloadEsisContainer>
  )
}

export const Arrow = ({
  currentRate,
  colour,
  disable,
}: {
  currentRate: boolean
  colour: ArrowColor
  disable: boolean
}) => {
  if (currentRate) return null
  if (colour === "green") {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M1.93406 8.34949L7.56812 15.3921C7.92804 15.842 8.6123 15.842 8.97222 15.3921L14.6063 8.34949C15.1439 7.6775 14.521 6.70691 13.6862 6.91563L9.27017 8.01963L9.27017 0.269632L7.27017 0.269632L7.27017 8.01963L2.85416 6.91563C2.01929 6.70691 1.39647 7.6775 1.93406 8.34949Z"
          fill={disable ? "#B5BEDC" : "#038352"}
        />
      </svg>
    )
  } else if (colour === "orange") {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M14.6079 7.65051L8.97387 0.607931C8.61395 0.158033 7.92969 0.158033 7.56977 0.607931L1.93571 7.65051C1.39812 8.32249 2.02094 9.29309 2.85581 9.08437L7.27182 7.98037L7.27182 15.7304L9.27182 15.7304L9.27182 7.98037L13.6878 9.08437C14.5227 9.29309 15.1455 8.3225 14.6079 7.65051Z"
          fill={disable ? "#B5BEDC" : "#FCC778"}
        />
      </svg>
    )
  } else {
    return null
  }
}

export const ValueContainer = styled.div({
  display: "flex",
  alignItems: "baseline",
  gap: 8,
})

const DownloadEsisContainer = styled.div({
  display: "flex",
  justifyContent: "center",
})

const DownloadEsisLink = styled.a({
  fontWeight: 600,
  fontSize: 20,
  margin: "16px 0",
  color: "#073ED9",
})

const DownloadEsisLinkDisabled = styled.p({
  fontWeight: 600,
  fontSize: 20,
  margin: "16px 0",
  color: "#6E717C",
})

const RateWrapper = styled.div<{ selected: boolean; disable: boolean }>`
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  background: #fff;
  padding: 24px;
  gap: 16px;
  flex: 1;
  border: ${({ selected }) => (selected ? "2px solid orange" : "none")};
  opacity: ${({ disable }) => (disable ? "0.7" : "1")};
`

const RateCardTitle = styled.div`
  font-weight: 600;
  font-size: 20px;
  margin-bottom: 8px;
`

const RateActionContainer = styled.div`
  width: 100%;
  margin: 10px 0;
  padding: 10px 30px;
  border-radius: 8px;
  background: #eef5f3;
  font-size: 18px;
  font-weight: 600;

  & > div {
    display: flex;
    align-items: center;
    color: #398156;

    justify-content: center;
    text-align: center;
    ::before {
      content: url(${IconTick});
    }
  }
`

const RateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  border-radius: 8px;
  padding: 8px;
  background: #f3f5fc;
`

const RateHighlight = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  justify-items: center;
  align-items: center;
  padding: 16px;
  text-align: center;
  width: 100%;

  background: #dde3f7;
  border-radius: 4px;

  & > div:first-of-type {
    font-size: 20px;
    font-weight: 600;
  }
`

const RateDetails = styled.ul`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  padding: 0 16px;
`

const RateCTAs = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  font-size: 18px;
  gap: 16px;
`
